import { Component, Input, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { CampaignHashtagPosts } from '@infrab4a/connect';
import { MessageService } from 'primeng/api';
import { CampaignHashtagPostsService } from 'src/app/connect-api/api/shop/campaign-hashtag-posts.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-glam-campaigns-post-form',
  templateUrl: './glam-campaigns-post-form.component.html',
  styleUrls: ['./glam-campaigns-post-form.component.scss']
})
export class GlamCampaignsPostFormComponent implements OnInit {
  @Input() campaignId: string;
  @Input() postId: string;

  private postsService = inject(CampaignHashtagPostsService);
  private messageService = inject(MessageService);
  private title = inject(Title);
  private router = inject(Router);

  post: CampaignHashtagPosts | null = null;
  isCreate = false;

  form = new FormGroup({
    postUrl: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    personAccount: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    personId: new FormControl<number>(null),
    consent: new FormControl(false, { nonNullable: true }),
    consentDate: new FormControl<Date>(null),
    createdAt: new FormControl<Date>({ value: null, disabled: true }),
    ipAddress: new FormControl('', { nonNullable: true }),
    userAgent: new FormControl('', { nonNullable: true }),
    platform: new FormControl('', { nonNullable: true }),
    approved: new FormControl(false, { nonNullable: true }),
    rating: new FormControl<number>(null, [Validators.min(0)]),
    status: new FormControl('pending', { nonNullable: true }),
    postAnalysis: new FormControl('', { nonNullable: true })
  });

  async ngOnInit(): Promise<void> {
    this.isCreate = !this.postId;

    if (this.isCreate) {
      this.title.setTitle('Novo post');
      return;
    }

    this.title.setTitle('Avaliar post');
    await this.loadPost();
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.isCreate) {
      await this.createPost();
      return;
    }

    await this.updatePost();
  }

  goBack(): void {
    this.router.navigate(['/configuracoes/glam/campaigns', this.campaignId]);
  }

  invalidControl(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control?.invalid && !!(control?.touched || control?.dirty);
  }

  private async createPost(): Promise<void> {
    const formValue = this.form.getRawValue();
    LoaderService.showLoader();
    try {
      const metadata = this.buildMetadata(formValue);
      await this.postsService.savePost({
        campaignId: this.campaignId,
        postUrl: formValue.postUrl,
        personAccount: formValue.personAccount,
        personId: formValue.personId,
        consent: formValue.consent,
        consentDate: formValue.consentDate,
        approved: formValue.approved,
        rating: formValue.rating,
        status: formValue.status,
        postAnalysis: formValue.postAnalysis,
        ...(metadata && { metadata })
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Salvo com sucesso',
        detail: 'O post foi criado.'
      });

      this.goBack();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async updatePost(): Promise<void> {
    if (!this.postId) {
      return;
    }

    const formValue = this.form.getRawValue();
    LoaderService.showLoader();
    try {
      await this.postsService.updatePost({
        id: this.postId,
        campaignId: this.campaignId,
        approved: formValue.approved,
        rating: formValue.rating,
        status: formValue.status,
        postAnalysis: formValue.postAnalysis,
        postEvaluationDate: this.post?.postEvaluationDate
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Salvo com sucesso',
        detail: 'A avaliação do post foi salva.'
      });

      this.goBack();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async loadPost(): Promise<void> {
    if (!this.postId) {
      return;
    }

    LoaderService.showLoader();
    try {
      this.post = await this.postsService.getPostById(this.postId);
      this.patchForm(this.post);
      this.disableIdentityFields();
    } catch (error) {
      AppDialogService.showErrorDialog(error, true);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private disableIdentityFields(): void {
    this.form.controls.postUrl.disable();
    this.form.controls.personAccount.disable();
    this.form.controls.personId.disable();
    this.form.controls.consent.disable();
    this.form.controls.consentDate.disable();
    this.form.controls.ipAddress.disable();
    this.form.controls.userAgent.disable();
    this.form.controls.platform.disable();
  }

  private patchForm(post: CampaignHashtagPosts): void {
    this.form.patchValue({
      postUrl: post.postUrl ?? '',
      personAccount: post.personAccount ?? '',
      personId: post.personId,
      consent: post.consent ?? false,
      consentDate: this.toDate(post.consentDate),
      createdAt: this.toDate(post.createdAt),
      ipAddress: post.metadata?.IPAddress ?? '',
      userAgent: post.metadata?.UserAgent ?? '',
      platform: post.metadata?.Platform ?? '',
      approved: post.approved ?? false,
      rating: post.rating,
      status: post.status ?? '',
      postAnalysis: post.postAnalysis ?? ''
    });
  }

  private buildMetadata(formValue: {
    ipAddress: string;
    userAgent: string;
    platform: string;
  }): CampaignHashtagPosts['metadata'] | undefined {
    const IPAddress = formValue.ipAddress?.trim();
    const UserAgent = formValue.userAgent?.trim();
    const Platform = formValue.platform?.trim();

    if (!IPAddress && !UserAgent && !Platform) {
      return undefined;
    }

    return {
      ...(IPAddress && { IPAddress }),
      ...(UserAgent && { UserAgent }),
      ...(Platform && { Platform })
    };
  }

  private toDate(value: Date | string | number | undefined): Date | null {
    if (!value) {
      return null;
    }

    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
}
